// HEAL-46a §6.3 — экран пакета.
//
// Полноценный экран со своим адресом, а не всплывающее окно: он переживает
// перезапуск приложения и открывается по ссылке. Модалку, которую можно смахнуть
// и потерять, здесь делать нельзя — за ней стоят десятки файлов человека.
//
// Список показывает ВСЕ выбранные файлы, включая отклонённые (D-25). Принятые
// приходят с сервера, отклонённые — из памяти устройства: строки для них не
// создаётся вовсе, а человек, выбравший пять файлов, должен видеть пять.
//
// Готовый файл становится доступен СРАЗУ, не дожидаясь остальных (D-17): хочешь —
// проверяй, пока другие обрабатываются; не хочешь — уходи и возвращайся. Ни один
// путь не заперт.

import { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileCheck2,
  Loader2,
  MinusCircle,
} from 'lucide-react';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { PushPermissionPrompt } from '@/components/push/PushPermissionPrompt';
import { WaitingHint } from '@/components/uploads/WaitingHint';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import { deleteUploadBatch, useUploadBatch } from '@/api/hooks/uploads';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { useToast } from '@/hooks/use-toast';
import {
  clearRejected,
  loadRejected,
  rejectedLabel,
  type RejectedFile,
} from '@/lib/batchOutcomes';
import { plural } from '@/components/uploads/PendingReviewBar';
import { setReviewOrigin, setUploadOrigin } from '@/lib/uploadNav';
import type { BatchFile, BatchFileState } from '@/api/types';

/** Подпись положения — словами, без жаргона состояний. */
export function stateLabel(file: BatchFile): string {
  switch (file.state) {
    case 'waiting_quota':
      return 'Распознаем завтра';
    case 'processing':
      return 'Обрабатывается';
    case 'awaiting_review':
      return 'Нужно проверить';
    case 'confirmed':
      return 'Готово';
    case 'reported':
      return 'Ждёт проверки';
    case 'failed':
      return 'Не удалось распознать';
  }
}

function StateIcon({ state }: { state: BatchFileState }) {
  switch (state) {
    case 'confirmed':
      return <CheckCircle2 className="h-5 w-5 shrink-0 text-primary-text" />;
    case 'failed':
      return <AlertCircle className="h-5 w-5 shrink-0 text-rose-600" />;
    case 'awaiting_review':
      return <ChevronRight className="h-5 w-5 shrink-0 text-amber-600" />;
    case 'waiting_quota':
      return <Clock className="h-5 w-5 shrink-0 text-gray-400" />;
    default:
      return <Loader2 className="h-5 w-5 shrink-0 animate-spin text-gray-400" />;
  }
}

/**
 * Строка списка — либо принятый файл (с сервера), либо отклонённый (из памяти
 * устройства). Порядок общий: как человек выбирал.
 */
type Row =
  | { kind: 'accepted'; position: number; file: BatchFile }
  | { kind: 'rejected'; position: number; file: RejectedFile };

export function mergeRows(files: BatchFile[], rejected: RejectedFile[]): Row[] {
  const rows: Row[] = [
    ...files.map((file, i) => ({
      kind: 'accepted' as const,
      position: file.position ?? i,
      file,
    })),
    ...rejected.map((file) => ({ kind: 'rejected' as const, position: file.position, file })),
  ];

  return rows.sort((a, b) => a.position - b.position);
}

/** Сводка словами: «5 файлов · 3 обработаны · 2 уже были». */
export function summarize(files: BatchFile[], rejected: RejectedFile[]): string {
  const total = files.length + rejected.length;
  const parts = [`${total} ${plural(total, 'файл', 'файла', 'файлов')}`];

  // «Обработан» и «нужно проверить» рядом читаются как противоречие: человек
  // понимает «обработан» как «готово, можно забыть». Поэтому распознанные, но
  // не подтверждённые называем тем, чего от них ждут.
  const awaiting = files.filter((f) => f.state === 'awaiting_review').length;
  if (awaiting > 0) parts.push(`${awaiting} ${plural(awaiting, 'ждёт', 'ждут', 'ждут')} проверки`);

  const confirmed = files.filter((f) => f.state === 'confirmed').length;
  if (confirmed > 0) parts.push(`${confirmed} ${plural(confirmed, 'подтверждён', 'подтверждены', 'подтверждены')}`);

  const dup = rejected.filter((r) => r.reason === 'duplicate').length;
  if (dup > 0) parts.push(`${dup} уже ${plural(dup, 'был', 'были', 'были')}`);

  const notSent = rejected.filter((r) => r.reason === 'not_sent').length;
  if (notSent > 0) parts.push(`${notSent} не ${plural(notSent, 'отправлен', 'отправлены', 'отправлены')}`);

  const badFile = rejected.filter(
    (r) => r.reason === 'too_large' || r.reason === 'unsupported_type',
  ).length;
  if (badFile > 0) parts.push(`${badFile} не ${plural(badFile, 'подошёл', 'подошли', 'подошли')}`);

  const failed = files.filter((f) => f.state === 'failed').length;
  if (failed > 0) parts.push(`${failed} не ${plural(failed, 'распознан', 'распознаны', 'распознаны')}`);

  return parts.join(' · ');
}

export function BatchScreen() {
  const [, params] = useRoute('/uploads/batch/:id');
  const [, setLocation] = useLocation();
  const batchId = Number(params?.id);

  const { data, isLoading, isError, error } = useUploadBatch(batchId);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Пакет разобран — исходы больше не нужны. Чистка в эффекте, а не в теле
  // рендера (круг ревью 2, находка 7): побочное действие при отрисовке
  // повторялось бы на каждом рендере и не отражалось в текущем кадре.
  const batchDone = data?.is_complete === true && data.awaiting_review_count === 0;
  useEffect(() => {
    if (batchDone) clearRejected(batchId);
  }, [batchDone, batchId]);
  const [deleting, setDeleting] = useState(false);
  const qc = useQueryClient();
  const { toast } = useToast();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const removed = await deleteUploadBatch(batchId);
      clearRejected(batchId);
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.uploads }),
        qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
        qc.invalidateQueries({ queryKey: queryKeys.biomarkers }),
      ]);
      toast({
        title: 'Пакет удалён',
        description: `Удалено анализов: ${removed}`,
        variant: 'success',
      });
      setLocation('/');
    } catch {
      toast({
        title: 'Не удалось удалить',
        description: 'Попробуйте ещё раз.',
        variant: 'destructive',
      });
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (isLoading) return <FullScreenSpinner />;

  if (isError || !data) {
    // HEAL-76 (D-5): «пакета нет» и «сбой загрузки» — разные вещи.
    //
    // До ленты сюда попадали в основном сразу после отправки, и «не удалось
    // открыть» было правдой. Теперь ссылка живёт в уведомлении год, а пакет
    // можно удалить в любой момент — в том числе с другого устройства, уже
    // после того, как лента отдала запись. Человек, открывший такую запись,
    // читал «не удалось открыть» как сбой приложения.
    //
    // Про удалённый и про чужой пакет говорим одной фразой намеренно:
    // раздельные тексты подсказали бы постороннему, что такой пакет существует.
    // Тот же приём, что для анализа в HEAL-81.
    const notFound = error?.response?.status === 404;

    return (
      <MobileFrame>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {notFound
              ? "Пакет не найден или у вас нет доступа."
              : "Не удалось открыть пакет."}
          </p>
          <Button onClick={() => setLocation("/")}>На главную</Button>
        </div>
      </MobileFrame>
    );
  }

  // Отклонённые сервер не знает: строки для них не создаётся вовсе. Берём из
  // памяти устройства и показываем ВМЕСТЕ с принятыми — человек выбрал пять
  // файлов и должен видеть пять, а не гадать, куда делся пятый (D-25).
  const rejected = loadRejected(batchId);
  const rows = mergeRows(data.files, rejected);

  // Сколько файлов не доехало: намерение против факта.
  //
  // `files_total` — это число файлов, ОТПРАВЛЕННЫХ на сервер: пакет создаётся уже
  // после местного отсева. Поэтому вычитать отсеянных по размеру и формату не
  // надо — их там и не было (круг ревью 2, находка 6). Вычитаем только то, что
  // сервер видел и отклонил.
  const serverRejected = rejected.filter(
    (r) => r.reason === 'duplicate' || r.reason === 'send_failed',
  ).length;

  // Удалённые вычитаются наравне с отклонёнными (найдено на устройстве 24.09).
  // Файл, который человек удалил сам, тоже уменьшает `files_received`, и без
  // этой поправки экран писал ему «1 файл не был отправлен — выберите их
  // заново», отправляя искать то, что он только что выбросил.
  const deleted = data.files_deleted ?? 0;
  const missing = Math.max(0, data.files_total - data.files_received - serverRejected - deleted);

  return (
    <MobileFrame>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="shrink-0 border-b border-border px-5 py-4">
          <h1 className="text-lg font-semibold leading-snug">Загрузка анализов</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{summarize(data.files, rejected)}</p>
          {/* HEAL-46b §9.3: пока есть чего ждать — говорим, что ждать не
              обязательно. Когда всё обработано, подпись исчезает: держать на
              экране совет уходить, когда уходить уже незачем, — шум. */}
          {!data.is_complete && <WaitingHint className="mt-1 text-sm text-muted-foreground" />}
        </header>

        <ul className="flex-1 overflow-y-auto px-5 py-2">
          {rows.map((row) =>
            row.kind === 'accepted' ? (
              <FileRow
                key={`a-${row.file.upload_id}`}
                title={row.file.title}
                label={stateLabel(row.file)}
                icon={<StateIcon state={row.file.state} />}
                onClick={
                  row.file.state === 'awaiting_review'
                    ? () => {
                        setReviewOrigin({ batchId });
                        setLocation(`/uploads/${row.file.upload_id}/review`);
                      }
                    : undefined
                }
              />
            ) : (
              <FileRow
                key={`r-${row.position}-${row.file.name}`}
                title={row.file.name}
                label={rejectedLabel(row.file.reason)}
                muted
                icon={
                  row.file.reason === 'duplicate' ? (
                    <FileCheck2 className="h-5 w-5 shrink-0 text-blue-500" />
                  ) : (
                    <MinusCircle className="h-5 w-5 shrink-0 text-gray-400" />
                  )
                }
                // У дубля тап ведёт на СУЩЕСТВУЮЩИЙ анализ: файл никуда не девался,
                // искать его не надо — надо показать, где он лежит.
                onClick={
                  row.file.reason === 'duplicate' && row.file.existingUploadId
                    ? () => {
                        // Пришли из пакета — в пакет и вернём.
                        setUploadOrigin({ batchId });
                        setLocation(`/uploads/${row.file.existingUploadId}`);
                      }
                    : undefined
                }
              />
            ),
          )}
        </ul>

        <footer className="shrink-0 border-t border-border px-5 py-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))]">
          {missing > 0 && (
            <p className="mb-2 text-center text-sm text-muted-foreground">
              {missing} {plural(missing, 'файл не был отправлен', 'файла не были отправлены', 'файлов не были отправлены')} —
              выберите их заново
            </p>
          )}
          {/* HEAL-46b D-30: предлагаем уведомление, пока есть чего ждать. Когда
              всё обработано, предложение бессмысленно — сообщать уже не о чем. */}
          <PushPermissionPrompt active={!data.is_complete} />
          {/* Счётчика «ждут проверки» здесь больше нет: о том же говорят шапка
              и подпись у каждой строки. Три места про одно — это шум. */}
          <Button
            variant="outline"
            className="w-full border-primary text-primary-text"
            onClick={() => setLocation('/')}
          >
            На главную
          </Button>
          {/* Удаление — текстом, не кнопкой, и это намеренно. Рамка сделала бы
              его равным по весу выходу, а разрушающее действие не должно
              выглядеть приглашением: человек пришёл разбирать анализы, а внизу
              его встречали бы две одинаковые кнопки, одна из которых стирает всё.
              Красный цвет даёт сигнал «осторожно», не повышая вес.

              Подтверждение называет число: «удалить пакет» звучит невинно, а
              стирается два десятка медицинских документов. */}
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="mt-3 block w-full py-1 text-center text-sm font-medium text-rose-600"
          >
            Удалить весь пакет
          </button>
        </footer>
      </div>

      <Dialog open={confirmDelete} onOpenChange={(open) => !deleting && setConfirmDelete(open)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Удалить {data.files_received}{' '}
              {plural(data.files_received, 'анализ', 'анализа', 'анализов')}?
            </DialogTitle>
            <DialogDescription className="text-left">
              Файлы и распознанные показатели будут стёрты. Восстановить их будет нельзя.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>
              Отмена
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Удаляем…' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileFrame>
  );
}

function FileRow({
  title,
  label,
  icon,
  onClick,
  muted,
}: {
  title: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  muted?: boolean;
}) {
  return (
    <li className="border-b border-border last:border-0">
      <button
        type="button"
        disabled={!onClick}
        onClick={onClick}
        className="flex w-full items-center gap-3 py-3.5 text-left disabled:cursor-default"
      >
        {icon}
        <span className="min-w-0 flex-1">
          <span className={['block truncate text-sm font-medium', muted ? 'text-muted-foreground' : ''].join(' ')}>
            {title}
          </span>
          <span className="block text-xs text-muted-foreground">{label}</span>
        </span>
        {onClick && <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
      </button>
    </li>
  );
}
